<script>
// components
import NextButton from 'dashboard/components-next/button/Button.vue';

// composables
import { useDebtStateFeedback } from 'dashboard/composables/useDebtStateFeedback';

export default {
  name: 'DebtStateBlock',
  components: {
    NextButton,
  },
  setup() {
    return useDebtStateFeedback();
  },
  computed: {
    shouldShow() {
      return this.hasState;
    },
  },
  watch: {
    conversationId: {
      immediate: true,
      handler() {
        this.resetEdit();
      },
    },
  },
};
</script>

<template>
  <div v-if="shouldShow" class="debt-state-block">
    <h6 class="debt-state-block--title">
      {{ $t('CONVERSATION.DEBT_STATE_WIDGET.TITLE') }}
    </h6>

    <div class="debt-state-block--body">
      <div class="debt-state-block--row">
        <span class="debt-state-block--label">
          {{ $t('CONVERSATION.DEBT_STATE_WIDGET.PREDICTED_LABEL') }}:
        </span>
        <span class="debt-state-block--value">{{ stateLabel }}</span>
      </div>

      <div v-if="confidenceLabel" class="debt-state-block--row">
        <span class="debt-state-block--label">
          {{ $t('CONVERSATION.DEBT_STATE_WIDGET.CONFIDENCE_LABEL') }}:
        </span>
        <span class="debt-state-block--value">{{ confidenceLabel }}</span>
      </div>

      <div v-if="sourceLabel" class="debt-state-block--row">
        <span
          class="debt-state-block--source-badge"
          :class="{ 'debt-state-block--source-badge--human': debtStateSource === 'human' }"
        >
          {{ sourceLabel }}
        </span>
      </div>
    </div>

    <div v-if="!isCorrecting" class="debt-state-block--actions">
      <NextButton
        xs
        faded
        color="blue"
        :disabled="isSubmitting"
        :label="$t('CONVERSATION.DEBT_STATE_WIDGET.CONFIRM')"
        @click="confirm"
      />
      <NextButton
        xs
        faded
        color="slate"
        :label="$t('CONVERSATION.DEBT_STATE_WIDGET.CORRECT')"
        @click="startCorrect"
      />
    </div>

    <div v-else class="debt-state-block--correct">
      <label class="debt-state-block--label" for="debt-state-block-select">
        {{ $t('CONVERSATION.DEBT_STATE_WIDGET.CORRECT_PROMPT') }}
      </label>
      <select
        id="debt-state-block-select"
        v-model="selectedState"
        class="debt-state-block--select"
      >
        <option value="" disabled>
          {{ $t('CONVERSATION.DEBT_STATE_WIDGET.CORRECT_PROMPT') }}
        </option>
        <option v-for="state in debtStates" :key="state" :value="state">
          {{ stateOptionLabel(state) }}
        </option>
      </select>
      <div class="debt-state-block--actions">
        <NextButton
          xs
          faded
          color="blue"
          :disabled="!selectedState || isSubmitting"
          :label="$t('CONVERSATION.DEBT_STATE_WIDGET.SAVE')"
          @click="submitCorrect"
        />
        <NextButton
          xs
          ghost
          color="slate"
          :label="$t('CONVERSATION.DEBT_STATE_WIDGET.CANCEL')"
          @click="cancelCorrect"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.debt-state-block {
  @apply flex flex-col gap-2 mt-4 px-3 py-3 rounded-lg border border-n-weak bg-n-background;

  .debt-state-block--title {
    @apply text-n-slate-11 text-xs font-semibold m-0;
  }

  .debt-state-block--body {
    @apply flex flex-col gap-1;
  }

  .debt-state-block--row {
    @apply flex items-center gap-1 text-xs text-n-slate-12;
  }

  .debt-state-block--label {
    @apply text-n-slate-11;
  }

  .debt-state-block--value {
    @apply font-medium;
  }

  .debt-state-block--source-badge {
    @apply text-xxs text-n-slate-10 bg-n-slate-3 rounded px-1.5 py-0.5;

    &--human {
      @apply text-n-amber-11 bg-n-amber-3;
    }
  }

  .debt-state-block--actions {
    @apply flex items-center gap-1 mt-1;
  }

  .debt-state-block--correct {
    @apply flex flex-col gap-1.5 mt-1;
  }

  .debt-state-block--select {
    @apply text-xs rounded-md border border-n-weak bg-n-background py-1 px-2;
  }
}
</style>
